import React, { useCallback, useRef, useState, useEffect } from "react";
import { connect } from "react-redux";
import Swal from "sweetalert2";
import { Button, Typography, Grid } from "@material-ui/core";
import Dialog49a from "../components/dialogs/dialog49a";
import DealCalculatorCard from "../components/layout/DealCalculatorCard";
import MyDealsTableSection from "../components/deals/MyDealsTableSection";
import dealsService from "../services/dealsService";
import adminService from "../services/adminService";
import * as customColors from "../theme/customColors";
import { isFeatureEnabled } from "../services/utils/featureAccess";
import { ignoreInvalidCharForNumber } from "../services/utils/utils";
import appreciationTaxIcon from "../assets/images-v2/Icons/masshevah.svg";
import btaxYamIcon from "../assets/images-v2/Icons/BTAX_yam.svg";
import deal49aIcon from "../assets/images-v2/Icons/49.svg";
import haklaiIcon from "../assets/images-v2/Icons/haklai.svg";

const DEAL_ROUTE_BY_TYPE = {
  btax: "/deal",
  btaxYam: "/just-prisa",
  btaxHaklai: "/haklai"
};

const DEAL_DIALOG_TITLE_BY_TYPE = {
  btax: "מחשבון מס שבח",
  btaxYam: "מחשבון ביטקס ים",
  btaxHaklai: "משק חקלאי"
};

const MyDealsContainer = (props) => {
  const { onLoadingChange } = props;
  const hasHaklaiAccess = isFeatureEnabled(props.enabledFeatures, "BTAX_HAKLAI_DEALS");
  const hasBtaxYam =
    props.softwareServiceType === "btaxYam" || (props.softwareServiceType === "btax" && props.abilities.includes("prisaExt"));
  const availableDealTabs = [
    {
      value: "btax",
      label: "עסקאות ביטקס",
      title: "עסקאות ביטקס",
      isVisible: props.softwareServiceType === "btax"
    },
    {
      value: "btaxYam",
      label: "ביטקס ים",
      title: "ביטקס ים",
      isVisible: hasBtaxYam
    },
    {
      value: "btaxHaklai",
      label: "משק חקלאי",
      title: "משק חקלאי",
      isVisible: props.softwareServiceType === "btax" && hasHaklaiAccess
    }
  ].filter((dealTab) => dealTab.isVisible);
  const defaultDealType = availableDealTabs[0]?.value || "btax";
  const [state, setState] = useState({
    isDialog49aOpen: false,
    officeLawyers: []
  });
  const [activeDealType, setActiveDealType] = useState(defaultDealType);
  const [deals, setDeals] = useState([]);
  const [dealsAllCount, setDealsAllCount] = useState(0);
  const [isDealsLoading, setIsDealsLoading] = useState(true);
  const [isOfficeLoading, setIsOfficeLoading] = useState(props.jobType === "manager");
  const [page, setPage] = useState(0);
  const [searchFilters, setSearchFilters] = useState({
    dealPrettyId: "",
    dealName: ""
  });
  const [dealsPrettyIdAndDesc, setDealsPrettyIdAndDesc] = useState([]);
  const dealsRequestId = useRef(0);

  const loadDeals = useCallback(async (dealType, isTotalCount = true, newPage = 0, filters = {}) => {
    const requestId = ++dealsRequestId.current;
    setIsDealsLoading(true);

    try {
      const response = await dealsService.getAllDeals({
        dealPrettyId: filters.dealPrettyId || "",
        dealName: filters.dealName || "",
        isTotalCount,
        softwareServiceType: dealType,
        skipAmount: newPage * 10
      });

      if (requestId !== dealsRequestId.current) return;
      if (!response.success) {
        setDeals([]);
        if (isTotalCount) setDealsAllCount(0);
        return;
      }

      setDeals(response.allDeals);
      if (isTotalCount) setDealsAllCount(response.dealsAmount ?? 0);
      if (dealType === "btax") {
        setDealsPrettyIdAndDesc(
          response.allDeals.map((deal) => ({
            _id: deal._id,
            prettyId: deal.prettyId,
            dealDescription: deal.manage?.dealDescription || "",
            belongToEmail: deal.belongToEmail,
            listOfSharedLawyers: deal.listOfSharedLawyers
          }))
        );
      } else {
        setDealsPrettyIdAndDesc([]);
      }
    } catch (error) {
      if (requestId === dealsRequestId.current) {
        setDeals([]);
        if (isTotalCount) setDealsAllCount(0);
      }
    } finally {
      if (requestId === dealsRequestId.current) setIsDealsLoading(false);
    }
  }, []);

  useEffect(() => {
    window["scrollTo"]({ top: 0, behavior: "smooth" });
    setIsOfficeLoading(props.jobType === "manager");
    if (props.jobType === "manager") {
      dealsService.getLawyersInOffice().then((response) => {
        if (response.success) {
          setState((previousState) => ({
            ...previousState,
            officeLawyers: response.lawyers
          }));
        }
      }).catch(() => {
        setState((previousState) => ({ ...previousState, officeLawyers: [] }));
      }).finally(() => setIsOfficeLoading(false));
    }
  }, [props.jobType]);

  useEffect(() => {
    onLoadingChange?.(isDealsLoading || isOfficeLoading);
  }, [isDealsLoading, isOfficeLoading, onLoadingChange]);

  useEffect(() => {
    const emptySearchFilters = {
      dealPrettyId: "",
      dealName: ""
    };
    setDeals([]);
    setDealsAllCount(0);
    setPage(0);
    setSearchFilters(emptySearchFilters);
    loadDeals(activeDealType, true, 0, emptySearchFilters);
  }, [activeDealType, loadDeals]);

  const handleSearchFilterChange = (key, value) => {
    const normalizedValue = key === "dealPrettyId" ? ignoreInvalidCharForNumber(value) : value;
    setSearchFilters((previousFilters) => ({
      ...previousFilters,
      [key]: normalizedValue
    }));
  };

  const validateSearchFilters = () => {
    if (searchFilters.dealPrettyId && searchFilters.dealPrettyId.length < 6) {
      Swal.fire("", "חיפוש לפי מספר עסקה חייב להיות לפחות 6 מספרים", "info");
      return false;
    }
    if (searchFilters.dealName && searchFilters.dealName.length < 3) {
      Swal.fire("", "חיפוש לפי שם עסקה חייב להיות לפחות 3 תוים", "info");
      return false;
    }
    return true;
  };

  const searchDeals = () => {
    if (!validateSearchFilters()) return;
    setPage(0);
    loadDeals(activeDealType, true, 0, searchFilters);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadDeals(activeDealType, false, newPage, searchFilters);
  };

  const initNewDeal = (dealType) => {
    if (dealType === "btaxHaklai" && !hasHaklaiAccess) return;

    Swal.fire({
      title: DEAL_DIALOG_TITLE_BY_TYPE[dealType] || "עסקה חדשה",
      html: `
        <div class="new-deal-dialog-label">שם העסקה</div>
      `,
      input: "text",
      inputPlaceholder: "יש למלא שם עסקה",
      showCancelButton: true,
      reverseButtons: true,
      buttonsStyling: false,
      cancelButtonText: "ביטול",
      confirmButtonText: "שמור",
      customClass: {
        popup: "new-deal-dialog-popup",
        title: "new-deal-dialog-title",
        htmlContainer: "new-deal-dialog-content",
        input: "new-deal-dialog-input",
        actions: "new-deal-dialog-actions",
        confirmButton: "new-deal-dialog-confirm-button",
        cancelButton: "new-deal-dialog-cancel-button",
        validationMessage: "new-deal-dialog-validation"
      },
      preConfirm: (dealDescription) => {
        if (!dealDescription) return Swal.showValidationMessage("יש להזין תיאור עסקה");
        return dealsService
          .initNewDeal(dealDescription, dealType)
          .then((res) => res)
          .catch((e) => Swal.showValidationMessage(`בקשה נדחתה: ${e}`));
      }
    }).then((result) => {
      if (result.isConfirmed && result.value.success) {
        let path = "";
        if (dealType === "btax") path = "/deal";
        if (dealType === "btaxYam") path = "/just-prisa";
        if (dealType === "btaxHaklai") path = "/haklai";
        props.history.push(path + "/calc/" + result.value.newId);
      }
    });
  };

  const init49aPopup = () => {
    setState((prev) => ({ ...prev, isDialog49aOpen: true }));
  };

  const importDeal = () => {
    let type;
    Swal.fire({
      title: "האם לייבא את העסקה?",
      input: "textarea",
      showCancelButton: true,
      cancelButtonText: "ביטול",
      confirmButtonText: "המשך",
      preConfirm: (dealData) => {
        if (!dealData) return Swal.showValidationMessage("יש להזין עסקה");
        let dealDataObj;
        try {
          dealDataObj = JSON.parse(dealData);
          type = dealDataObj.softwareServiceType;
          if (type === "btaxHaklai") {
            return Swal.showValidationMessage("ייבוא עסקת ביטקס חקלאי אינו נתמך במסך זה");
          }
        } catch (e) {
          return Swal.showValidationMessage("פורמט JSON לא תקין");
        }
        return adminService
          .importDeal(dealDataObj)
          .then((res) => res)
          .catch((e) => Swal.showValidationMessage(`בקשה נדחתה: ${e}`));
      }
    }).then((result) => {
      if (result.isConfirmed && result.value.success) {
        let path = "";
        if (type === "btax") path = "/deal";
        if (type === "btaxYam") path = "/just-prisa";
        if (type === "btaxHaklai") path = "/haklai";
        props.history.push(path + "/calc/" + result.value.newId);
      }
    });
  };

  const openDeal = (deal) => {
    const isValidDeal = deal?._id?.length === 24;
    if (!isValidDeal) return Swal.fire("", "אירעה שגיאה", "error");
    if (activeDealType === "btaxHaklai" && !hasHaklaiAccess) return;

    props.history.push(`${DEAL_ROUTE_BY_TYPE[activeDealType]}/calc/${deal._id}`);
  };

  const copyDeal = (deal) => {
    const isValidDeal = deal?._id?.length === 24;
    if (!isValidDeal) return;
    if (activeDealType === "btaxHaklai" && !hasHaklaiAccess) return;

    dealsService
      .copyDeal(deal._id)
      .then((response) => props.history.push(`${DEAL_ROUTE_BY_TYPE[activeDealType]}/calc/${response.newId}`))
      .catch((error) => Swal.showValidationMessage(`בקשה נדחתה: ${error}`));
  };

  const deleteDeal = (deal) => {
    Swal.fire({
      icon: "warning",
      title: "האם אתה בטוח שברצונך למחוק",
      html: `<div class="btax-confirm-dialog-subtitle">את עסקה מספר ${deal.prettyId}</div>`,
      showCancelButton: true,
      reverseButtons: true,
      buttonsStyling: false,
      cancelButtonText: "ביטול",
      confirmButtonText: "מחק",
      customClass: {
        popup: "btax-confirm-dialog-popup",
        title: "btax-confirm-dialog-title",
        htmlContainer: "btax-confirm-dialog-content",
        icon: "btax-confirm-dialog-icon",
        actions: "btax-confirm-dialog-actions",
        confirmButton: "btax-confirm-dialog-confirm",
        cancelButton: "btax-confirm-dialog-cancel",
        validationMessage: "btax-confirm-dialog-validation"
      },
      preConfirm: () => {
        if (!deal._id) return Swal.showValidationMessage("מחיקה נכשלה");
        return dealsService
          .deleteDeal(deal._id)
          .then((response) => response)
          .catch((error) => Swal.showValidationMessage(`בקשה נדחתה: ${error}`));
      }
    }).then((result) => {
      if (result.isConfirmed && result.value.success) {
        Swal.fire({
          icon: "success",
          title: `עסקה מספר ${deal.prettyId} נמחקה בהצלחה`,
          confirmButtonText: "המשך",
          buttonsStyling: false,
          customClass: {
            popup: "btax-confirm-dialog-popup is-success",
            title: "btax-confirm-dialog-title",
            icon: "btax-confirm-dialog-icon is-success",
            actions: "btax-confirm-dialog-actions",
            confirmButton: "btax-confirm-dialog-continue"
          },
          preConfirm: () => {}
        }).then((result) => {
          if (result.isConfirmed) {
            setPage(0);
            loadDeals(activeDealType, true, 0, searchFilters);
          }
        });
      }
    });
  };

  const transferDeal = async (newOwnerEmail, dealIdBeingSharedOrTransferred, dealNewLawyerName) => {
    try {
      // Check if deal is shared with someone first and prevent saving if its the case
      const currentDeal = dealsPrettyIdAndDesc.find((currentDeal) => currentDeal._id === dealIdBeingSharedOrTransferred);
      if (currentDeal && currentDeal.listOfSharedLawyers && currentDeal.listOfSharedLawyers.length) {
        Swal.fire("", "רק עסקאות ללא שיתוף יכולות להיות מועברות לערך דין אחר", "warning");
        return;
      }

      const response = await dealsService.transferDeal(newOwnerEmail, dealIdBeingSharedOrTransferred, dealNewLawyerName);
      if (response.success) {
        Swal.fire("בוצע בהצלחה", "העסקה הועברה", "success");
        setDeals((previousDeals) =>
          previousDeals.map((deal) =>
            deal._id === dealIdBeingSharedOrTransferred ? { ...deal, fullName: dealNewLawyerName, belongToEmail: newOwnerEmail } : deal
          )
        );
        setDealsPrettyIdAndDesc((previousDeals) =>
          previousDeals.map((deal) => (deal._id === dealIdBeingSharedOrTransferred ? { ...deal, belongToEmail: newOwnerEmail } : deal))
        );
        return;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const shareDeal = async (newOwnerEmail, dealIdBeingSharedOrTransferred) => {
    try {
      const response = await dealsService.shareDeal(newOwnerEmail, dealIdBeingSharedOrTransferred);
      if (response.success) {
        Swal.fire("", "שותף בהצלחה", "success");
        setDealsPrettyIdAndDesc((previousDeals) =>
          previousDeals.map((deal) =>
            deal._id === dealIdBeingSharedOrTransferred
              ? { ...deal, listOfSharedLawyers: [...(deal.listOfSharedLawyers || []), newOwnerEmail] }
              : deal
          )
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const cancelShareDeal = async (ownerToRemove, dealIdBeingSharedOrTransferred) => {
    try {
      const response = await dealsService.cancelShareDeal(ownerToRemove, dealIdBeingSharedOrTransferred);
      if (response.success) {
        Swal.fire("", "הוסר שיתוף בהצלחה", "success");
        setDealsPrettyIdAndDesc((previousDeals) =>
          previousDeals.map((deal) =>
            deal._id === dealIdBeingSharedOrTransferred
              ? {
                  ...deal,
                  listOfSharedLawyers: (deal.listOfSharedLawyers || []).filter(
                    (sharedLawyerEmail) => sharedLawyerEmail !== ownerToRemove
                  )
                }
              : deal
          )
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const calculatorDescription = "חישוב מס שבח במכירת מקרקעין בישראל, כולל דירות מגורים.";
  const calculatorCards = [
    {
      id: "btax",
      title: "מחשבון מס שבח",
      description: calculatorDescription,
      icon: appreciationTaxIcon,
      isVisible: props.softwareServiceType === "btax",
      onClick: () => initNewDeal("btax")
    },
    {
      id: "btaxYam",
      title: "מחשבון ביטקס ים",
      description: "בדיקת כדאיות לפריסת מס שבח על בסיס שומה קיימת, שלא במועד העסקה.",
      icon: btaxYamIcon,
      isVisible: hasBtaxYam,
      onClick: () => initNewDeal("btaxYam")
    },
    {
      id: "49a",
      title: "עסקת 49ה׳",
      description: "חישוב ובחינת מכירת שתי דירות לצורך רכישת דירה אחת בהתאם לסעיף 49(ה).",
      icon: deal49aIcon,
      isVisible: props.softwareServiceType === "btax",
      onClick: init49aPopup
    },
    {
      id: "haklai",
      title: "משק חקלאי",
      description: "חישובי מס שבח במכירת משק חקלאי.",
      icon: haklaiIcon,
      disabled: !hasHaklaiAccess,
      isVisible: props.softwareServiceType === "btax",
      onClick: () => initNewDeal("btaxHaklai")
    }
  ];

  return (
    <Grid container direction="column" spacing={2}>
      <Grid container item direction="row" justify="flex-start" alignItems="flex-end" style={{ minHeight: "105px" }}>
        <Typography style={{ color: "#1e3d66", fontWeight: 700 }} variant="h4">
          מחשבוני ביטקס
        </Typography>
      </Grid>
      <Grid item>
        <Grid container direction="row" justify="flex-start" alignItems="center" style={{ gap: "16px" }}>
          {calculatorCards
            .filter((calculatorCard) => calculatorCard.isVisible)
            .map((calculatorCard) => (
              <Grid item key={calculatorCard.id}>
                <DealCalculatorCard
                  description={calculatorCard.description}
                  disabled={calculatorCard.disabled}
                  icon={calculatorCard.icon}
                  onClick={calculatorCard.onClick}
                  title={calculatorCard.title}
                />
              </Grid>
            ))}
          {props.role === "admin" ? (
            <Grid item>
              <Button
                color="secondary"
                onClick={importDeal}
                size="small"
                style={{ color: customColors.white, fontWeight: 700 }}
                variant="contained"
              >
                ייבא עסקה
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
      {props.softwareServiceType === "btax" ? (
        <Dialog49a
          open={state.isDialog49aOpen}
          label="מכירת שתי דירות בפטור - 49(ה)"
          onClose={() => setState((prev) => ({ ...prev, isDialog49aOpen: false }))}
        />
      ) : null}
      <Grid item>
        <MyDealsTableSection
          activeDealType={activeDealType}
          cancelShareDeal={cancelShareDeal}
          currentLawyerEmail={props.email}
          deals={deals}
          dealsAllCount={dealsAllCount}
          dealsPrettyIdAndDesc={dealsPrettyIdAndDesc}
          isLoading={isDealsLoading}
          officeLawyers={state.officeLawyers}
          onCopyDeal={copyDeal}
          onDeleteDeal={deleteDeal}
          onOpenDeal={openDeal}
          onPageChange={handlePageChange}
          onSearch={searchDeals}
          onSearchFilterChange={handleSearchFilterChange}
          onTabChange={setActiveDealType}
          page={page}
          searchFilters={searchFilters}
          shareDeal={shareDeal}
          tabs={availableDealTabs}
          transferDeal={transferDeal}
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    belongToCardNum: state.loggedinReducer.belongToCardNum,
    enabledFeatures: state.loggedinReducer.enabledFeatures,
    email: state.loggedinReducer.email,
    fullName: state.loggedinReducer.fullName,
    abilities: state.loggedinReducer.abilities,
    softwareServiceType: state.loggedinReducer.softwareServiceType,
    jobType: state.loggedinReducer.jobType,
    role: state.loggedinReducer.role
  };
};

export default connect(mapStateToProps)(MyDealsContainer);
